import { apiFetch } from './apiUtils';

/**
 * Magic code detection for common file types
 * @param {Uint8Array} bytes - First few bytes of the file
 * @returns {string|null} - MIME type or null if not detected
 */
export const detectFileTypeFromBytes = (bytes) => {
  // Convert bytes to hex string for easier comparison
  const hex = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // PDF files start with %PDF (25 50 44 46)
  if (hex.startsWith('25504446')) {
    return 'application/pdf';
  }
  
  // JPEG files start with FF D8 FF
  if (hex.startsWith('ffd8ff')) {
    return 'image/jpeg';
  }
  
  // PNG files start with 89 50 4E 47 0D 0A 1A 0A
  if (hex.startsWith('89504e470d0a1a0a')) {
    return 'image/png';
  }
  
  // GIF files start with GIF87a or GIF89a (47 49 46 38 37 61 or 47 49 46 38 39 61)
  if (hex.startsWith('474946383761') || hex.startsWith('474946383961')) {
    return 'image/gif';
  }
  
  // WebP files start with RIFF....WEBP (52 49 46 46 ... 57 45 42 50)
  if (hex.startsWith('52494646') && hex.includes('57454250')) {
    return 'image/webp';
  }
  
  // BMP files start with BM (42 4D)
  if (hex.startsWith('424d')) {
    return 'image/bmp';
  }
  
  // TIFF files start with II*\0 (49 49 2A 00) or MM\0* (4D 4D 00 2A)
  if (hex.startsWith('49492a00') || hex.startsWith('4d4d002a')) {
    return 'image/tiff';
  }
  
  // SVG files start with <?xml or <svg (3C 3F 78 6D 6C or 3C 73 76 67)
  if (hex.startsWith('3c3f786d6c') || hex.startsWith('3c737667')) {
    return 'image/svg+xml';
  }
  
  // HEIC files have "ftyp" at bytes 4-7 followed by "heic" or "heix" at bytes 8-11
  // Pattern: ....ftyp + heic/heix (first 4 bytes can vary, then 66747970 + 68656963/68656978)
  if (hex.length >= 24) {
    const ftypIndex = hex.indexOf('66747970'); // "ftyp" in hex
    if (ftypIndex >= 8 && ftypIndex <= 16) { // ftyp should be around bytes 4-8
      const afterFtyp = hex.substring(ftypIndex + 8, ftypIndex + 16);
      if (afterFtyp === '68656963' || afterFtyp === '68656978') { // "heic" or "heix"
        return 'image/heic';
      }
    }
  }
  
  // Return null if no magic code matches
  return null;
};

/**
 * Detect if a MIME type represents an image
 * @param {string} contentType - MIME type
 * @returns {boolean}
 */
export const isImageType = (contentType) => {
  return contentType?.startsWith('image/');
};

/**
 * Detect if a MIME type represents a PDF
 * @param {string} contentType - MIME type
 * @returns {boolean}
 */
export const isPdfType = (contentType) => {
  return contentType === 'application/pdf';
};

/**
 * Get file content type and presigned URL with proper detection
 * @param {string} s3Key - S3 key for the file
 * @param {string} accessCode - Access code for authentication
 * @returns {Promise<{url: string, contentType: string}>}
 */
export const getFileUrlAndType = async (s3Key, accessCode) => {
  try {
    // Get presigned URL
    const res = await apiFetch(
      `${import.meta.env.VITE_API_URL}/flight-admin/passport/${s3Key}?access_code=${accessCode}`
    );

    if (res.status !== 'success' || !res.url) {
      throw new Error('Could not get presigned URL');
    }

    // Detect content type from URL extension (avoiding CORS issues)
    let contentType = null;
    const urlPath = new URL(res.url).pathname;
    const extension = urlPath.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        contentType = 'application/pdf';
        break;
      case 'jpg':
      case 'jpeg':
        contentType = 'image/jpeg';
        break;
      case 'png':
        contentType = 'image/png';
        break;
      case 'gif':
        contentType = 'image/gif';
        break;
      case 'webp':
        contentType = 'image/webp';
        break;
      case 'bmp':
        contentType = 'image/bmp';
        break;
      case 'tiff':
      case 'tif':
        contentType = 'image/tiff';
        break;
      case 'svg':
        contentType = 'image/svg+xml';
        break;
      case 'heic':
        contentType = 'image/heic';
        break;
      default:
        contentType = 'application/octet-stream';
    }
    
    console.log('Detected content type from extension:', extension, '->', contentType);
    
    return {
      url: res.url,
      contentType
    };
  } catch (error) {
    console.error('Failed to get file URL and type:', error);
    throw error;
  }
};

/**
 * Load an image with proper error handling and CORS support
 * @param {string} imageUrl - URL of the image to load
 * @returns {Promise<HTMLImageElement>}
 */
export const loadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => resolve(img);
    
    img.onerror = (error) => {
      console.error('Failed to load image with CORS:', imageUrl, error);
      
      // Try loading without CORS if the first attempt fails
      const imgNoCors = new Image();
      imgNoCors.onload = () => resolve(imgNoCors);
      imgNoCors.onerror = (noCorsError) => {
        console.error('Failed to load image without CORS:', imageUrl, noCorsError);
        reject(new Error(`Failed to load image: ${imageUrl}`));
      };
      
      // Don't set crossOrigin for the fallback attempt
      imgNoCors.src = imageUrl;
    };
    
    // Try with CORS first
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
  });
};

/**
 * Load an image from S3 with proper type detection and error handling
 * @param {string} s3Key - S3 key for the image
 * @param {string} accessCode - Access code for authentication
 * @returns {Promise<{image: HTMLImageElement, contentType: string}>}
 */
export const loadImageFromS3 = async (s3Key, accessCode) => {
  const { url, contentType } = await getFileUrlAndType(s3Key, accessCode);
  
  if (!isImageType(contentType)) {
    throw new Error(`File is not an image: ${contentType}`);
  }
  
  const image = await loadImage(url);
  
  return {
    image,
    contentType
  };
};